import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator, faLocationDot, faArrowRight,
  faWeightHanging, faCircleInfo, faTriangleExclamation,
  faTag
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import '../styles/RateCalculator.css';

const accessorialRates = [
  { service: 'Liftgate Pickup',          rate: 75.00 },
  { service: 'Liftgate Delivery',        rate: 75.00 },
  { service: 'Inside Pickup',            rate: 85.00 },
  { service: 'Inside Delivery',          rate: 85.00 },
  { service: 'Residential Pickup',       rate: 75.00 },
  { service: 'Residential Delivery',     rate: 75.00 },
  { service: 'Limited Access Pickup',    rate: 75.00 },
  { service: 'Limited Access Delivery',  rate: 75.00 },
  { service: 'Notification/Appointment', rate: 35.00 },
  { service: 'Sort and Segregate',       rate:  1.50, unit: 'per piece'      },
  { service: 'Detention',                rate: 35.00, unit: 'per 15 minutes' },
  { service: 'Storage',                  rate: 25.00, unit: 'per day'        },
];

function RateCalculator() {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [rate,    setRate]    = useState(null);
  const [formData, setFormData] = useState({
    fCity: '', fZip: '',
    tCity: '', tZip: '',
    ttw: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateRate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setRate(null);
    try {
      const { data } = await api.post('/api/bols/calculate-rate', {
        origin:      { city: formData.fCity, zip: formData.fZip },
        destination: { city: formData.tCity, zip: formData.tZip },
        freightItems: [{ pieces: 1, weight: formData.ttw }]
      });
      if (data.success) {
        setRate({ ...data, fromCity: formData.fCity, toCity: formData.tCity, weight: formData.ttw });
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to calculate rate');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="rc-page">
      {/* Header */}
      <div className="rc-page-header">
        <h1 className="rc-page-title">Rate Calculator</h1>
        <p className="rc-page-subtitle">Get an instant freight rate estimate between two locations.</p>
      </div>

      {/* Notice */}
      <div className="rc-notice">
        <FontAwesomeIcon icon={faCircleInfo} className="rc-notice-icon" />
        <span>
          This tool provides an estimated rate and is <strong>not a guaranteed quote</strong>.
          Your shipment may be subject to additional accessorial and volumetric charges.
        </span>
      </div>

      {/* Two-column layout */}
      <div className="rc-layout">

        {/* ── LEFT: Calculator form ── */}
        <div className="rc-panel">
          <div className="rc-panel-header">
            <div className="rc-panel-icon"><FontAwesomeIcon icon={faCalculator} /></div>
            <h6>Shipment Details</h6>
          </div>
          <div className="rc-panel-body">
            <form onSubmit={calculateRate}>
              {/* Route inputs */}
              <div className="rc-route">
                {/* Origin */}
                <div className="rc-route-point">
                  <div className="rc-route-dot-col">
                    <div className="rc-route-dot filled" />
                    <div className="rc-route-line" />
                  </div>
                  <div className="rc-route-fields">
                    <div className="rc-field-label">Origin</div>
                    <div className="rc-field-row">
                      <input
                        className="rc-input"
                        type="text"
                        name="fCity"
                        value={formData.fCity}
                        onChange={handleChange}
                        placeholder="City"
                        required
                      />
                      <input
                        className="rc-input zip"
                        type="text"
                        name="fZip"
                        value={formData.fZip}
                        onChange={handleChange}
                        placeholder="ZIP"
                        maxLength={5}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Destination */}
                <div className="rc-route-point">
                  <div className="rc-route-dot-col">
                    <div className="rc-route-dot" />
                  </div>
                  <div className="rc-route-fields">
                    <div className="rc-field-label">Destination</div>
                    <div className="rc-field-row">
                      <input
                        className="rc-input"
                        type="text"
                        name="tCity"
                        value={formData.tCity}
                        onChange={handleChange}
                        placeholder="City"
                        required
                      />
                      <input
                        className="rc-input zip"
                        type="text"
                        name="tZip"
                        value={formData.tZip}
                        onChange={handleChange}
                        placeholder="ZIP"
                        maxLength={5}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Weight */}
              <div className="rc-weight-row">
                <FontAwesomeIcon icon={faWeightHanging} style={{ color: '#94a3b8', fontSize: '0.85rem' }} />
                <span className="rc-weight-label">Total Weight</span>
                <input
                  className="rc-input weight"
                  type="number"
                  name="ttw"
                  value={formData.ttw}
                  onChange={handleChange}
                  placeholder="0"
                  min="1"
                  required
                />
                <span className="rc-weight-unit">lbs</span>
              </div>

              <button type="submit" className="rc-btn" disabled={loading}>
                {loading
                  ? <><Spinner animation="border" size="sm" /> Calculating…</>
                  : <><FontAwesomeIcon icon={faCalculator} /> Calculate Rate</>}
              </button>
            </form>
          </div>
        </div>

        {/* ── RIGHT: Results ── */}
        <div className="rc-panel">
          <div className="rc-panel-header">
            <div className="rc-panel-icon"><FontAwesomeIcon icon={faTag} /></div>
            <h6>Rate Estimate</h6>
          </div>
          <div className="rc-panel-body">
            {!rate && !error && !loading && (
              <div className="rc-empty">
                <div className="rc-empty-icon"><FontAwesomeIcon icon={faLocationDot} /></div>
                <div className="rc-empty-text">Enter origin, destination, and weight<br />then click Calculate Rate.</div>
              </div>
            )}

            {loading && (
              <div className="rc-empty">
                <Spinner animation="border" style={{ color: 'var(--tenant-primary-color, #3b82f6)', width: '2rem', height: '2rem' }} />
                <div className="rc-empty-text" style={{ marginTop: 14 }}>Fetching rates…</div>
              </div>
            )}

            {error && !loading && (
              <div className="rc-empty" style={{ color: '#ef4444' }}>
                <div className="rc-empty-icon" style={{ opacity: 1 }}><FontAwesomeIcon icon={faTriangleExclamation} /></div>
                <div className="rc-empty-text" style={{ color: '#ef4444' }}>{error}</div>
              </div>
            )}

            {rate && !loading && (
              <div className="rc-results">
                {/* Route summary */}
                <div className="rc-result-route">
                  <span className="rc-result-city">{rate.fromCity || 'Origin'}</span>
                  <FontAwesomeIcon icon={faArrowRight} className="rc-result-arrow" />
                  <span className="rc-result-city">{rate.toCity || 'Destination'}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem' }}>{rate.weight} lbs</span>
                </div>

                {/* Line items */}
                <div className="rc-result-rows">
                  <div className="rc-result-row">
                    <span>Base Rate</span>
                    <span>${fmt(rate.rate)}</span>
                  </div>
                  <div className="rc-result-row">
                    <span>Fuel Surcharge (FSC)</span>
                    <span>${fmt(rate.fsc)}</span>
                  </div>
                </div>

                {/* Total */}
                <div className="rc-result-total">
                  <span className="rc-result-total-label">Estimated Total</span>
                  <span className="rc-result-total-value">${fmt(rate.total)}</span>
                </div>

                <p className="rc-disclaimer">
                  * Estimate only. Actual charges depend on shipment specifics, accessorials, and current fuel rates.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Accessorial Rates ── */}
      <div className="rc-accessorial">
        <h2 className="rc-accessorial-title">Accessorial Services</h2>
        <div className="rc-accessorial-grid">
          {accessorialRates.map((item, i) => (
            <div key={i} className="rc-acc-card">
              <span className="rc-acc-service">{item.service}</span>
              <div className="rc-acc-price">
                <span className="rc-acc-amount">${fmt(item.rate)}</span>
                {item.unit && <span className="rc-acc-unit">{item.unit}</span>}
              </div>
            </div>
          ))}
        </div>
        <p className="rc-accessorial-note">
          * Additional charges may apply based on specific requirements. Contact customer service for details.
        </p>
      </div>
    </div>
  );
}

export default RateCalculator;
